import * as React from 'react';
import { DefaultButton, Link, MessageBar, MessageBarType, PrimaryButton } from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IAuditLog } from '../../../engine/AuditLog';
import { ChangeEngine } from '../../../engine/ChangeEngine';
import type { IDriveItemRef, IDriveWriter } from '../../../engine/DriveWriter';
import { generateIntakePlan, withIntake } from '../../../intake/IntakeMatcher';
import type { IChangeDecision, IOrganizationPlan } from '../../../models/Plan';
import type { IScanResult } from '../../../models/ScanResult';
import { type IResolvedChange, resolveChange } from '../../../plan/PlanSimulator';
import { INTAKE_FOLDER_NAME } from '../../../services/ScannerService';
import { describeGraphError } from '../../../utils/errors';
import { formatNumber } from '../../../utils/format';
import type { ITelemetry } from '../../../utils/telemetry';
import ApplyPanel from './ApplyPanel';
import ChangeCard from './ChangeCard';

export interface IIntakeViewProps {
  scan: IScanResult;
  auditLog: IAuditLog;
  createWriter: () => IDriveWriter;
  userName: string;
  allowChanges: boolean;
  telemetry: ITelemetry;
  onRescan: () => void;
}

/**
 * New files dropped into _Intake, each with a suggested home learned from the library's
 * existing folders. Nothing moves until a person approves and applies.
 */
export default function IntakeView(props: IIntakeViewProps): React.ReactElement {
  const { scan, auditLog, createWriter, userName, allowChanges, telemetry, onRescan } = props;
  const full: IScanResult = React.useMemo(() => withIntake(scan), [scan]);
  const plan: IOrganizationPlan = React.useMemo(() => generateIntakePlan(scan), [scan]);
  const [decisions, setDecisions] = React.useState<{ [changeId: string]: IChangeDecision }>({});
  const [applying, setApplying] = React.useState<{ engine: ChangeEngine; changes: IResolvedChange[] } | undefined>(undefined);
  const [creating, setCreating] = React.useState<boolean>(false);
  const [createError, setCreateError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => { setDecisions({}); }, [scan]);
  React.useEffect(() => {
    if (scan.intake) {
      telemetry.event('intake.suggested', {
        libraryId: scan.library.id,
        files: plan.changes.length,
        suggested: plan.changes.filter(c => !c.needsInput).length
      });
    }
  }, [plan, scan, telemetry]);

  const createIntakeFolder = (): void => {
    setCreating(true);
    setCreateError(undefined);
    const writer: IDriveWriter = createWriter();
    const now: string = new Date().toISOString();
    auditLog.ensure()
      .then(() => writer.getRoot())
      .then((root: IDriveItemRef) => writer.createFolder(root.id, INTAKE_FOLDER_NAME).then(folder => ({ root, folder })))
      .then(({ root, folder }) => auditLog.write({
        operationId: `OP-${now.substring(0, 10).replace(/-/g, '')}-INTK-0001`,
        batchId: `SETUP-${now.substring(0, 19).replace(/[-:T]/g, '')}-INTK`,
        changeId: '', action: 'createFolder', itemKind: 'folder', itemId: folder.id,
        libraryId: scan.library.id, libraryName: scan.library.name,
        sourcePath: '', sourceParentId: '', originalName: '',
        destinationPath: INTAKE_FOLDER_NAME, destinationParentId: root.id, newName: INTAKE_FOLDER_NAME,
        reason: 'Intake folder for new files, created on request.', status: 'success',
        reversible: false, user: userName, timestamp: now
      }))
      .then(() => { telemetry.event('intake.folderCreated', { libraryId: scan.library.id }); onRescan(); })
      .catch(error => {
        telemetry.error('intake.folderCreateFailed', error, { libraryId: scan.library.id });
        setCreateError(describeGraphError(error));
      })
      .then(() => setCreating(false), () => setCreating(false));
  };

  if (!scan.intake) {
    return (
      <div className={styles.plan}>
        <h3 className={styles.sectionTitle}>Intake</h3>
        <p className={styles.issueListDescription}>
          Keep the library tidy after the cleanup: people save new files into one <strong>{INTAKE_FOLDER_NAME}</strong> folder,
          and SharePoint Organizer suggests where each one belongs, based on where similar files already live.
          Nothing moves until you approve it.
        </p>
        {createError && <MessageBar messageBarType={MessageBarType.error} isMultiline>{createError}</MessageBar>}
        {!allowChanges && (
          <MessageBar isMultiline>
            <strong>Recommend-only mode.</strong> To create the intake folder, turn on <strong>Allow changes to this site</strong>.
          </MessageBar>
        )}
        <div className={styles.changeActions}>
          <PrimaryButton
            text={creating ? 'Creating…' : `Create the ${INTAKE_FOLDER_NAME} folder`}
            iconProps={{ iconName: 'NewFolder' }}
            disabled={!allowChanges || creating}
            onClick={createIntakeFolder}
          />
        </div>
      </div>
    );
  }

  const resolved: IResolvedChange[] = plan.changes.map(c => resolveChange(full, c, decisions[c.id]));
  const ready: IResolvedChange[] = resolved.filter(r => r.status === 'approved' && !r.error);
  const approvable: string[] = resolved.filter(r => r.status === 'pending' && !r.error).map(r => r.change.id);
  const suggested: number = plan.changes.filter(c => !c.needsInput).length;

  const openApply = (): void => {
    const engine: ChangeEngine = new ChangeEngine({ scan: full, plan, writer: createWriter(), audit: auditLog, user: userName });
    setApplying({ engine, changes: ready });
  };

  return (
    <div className={styles.plan}>
      <h3 className={styles.sectionTitle}>
        Intake <span className={styles.sectionCount}>{formatNumber(plan.changes.length)} {plan.changes.length === 1 ? 'file' : 'files'} waiting</span>
      </h3>
      <p className={styles.issueListDescription}>
        Files in <Link href={scan.intake.folder.webUrl} target="_blank" rel="noreferrer">{INTAKE_FOLDER_NAME}</Link>, each with a
        suggested home learned from where similar files already live.
        {plan.changes.length > 0 && ` ${formatNumber(suggested)} of ${formatNumber(plan.changes.length)} ${suggested === 1 ? 'has' : 'have'} a suggestion` +
          (suggested < plan.changes.length ? '; the rest need you to choose a folder.' : '.')}
        {' '}Filed files can be undone from History.
      </p>
      {!allowChanges && (
        <MessageBar isMultiline>
          <strong>Recommend-only mode.</strong> You can review suggestions; to file them, turn on <strong>Allow changes to this site</strong>.
        </MessageBar>
      )}

      {plan.changes.length === 0 ? (
        <MessageBar>No files are waiting. Save or upload new files to the {INTAKE_FOLDER_NAME} folder, then rescan.</MessageBar>
      ) : (
        <>
          <div className={styles.planToolbar}>
            <span className={styles.planProgress}>{formatNumber(ready.length)} ready to file</span>
            <DefaultButton
              text={`Approve all suggestions (${formatNumber(approvable.length)})`}
              iconProps={{ iconName: 'CheckList' }}
              disabled={approvable.length === 0}
              onClick={() => setDecisions(prev => {
                const next: { [id: string]: IChangeDecision } = { ...prev };
                for (const id of approvable) {
                  next[id] = { ...(prev[id] ?? {}), status: 'approved' };
                }
                return next;
              })}
            />
            <PrimaryButton
              text={`File approved (${formatNumber(ready.length)})`}
              iconProps={{ iconName: 'Play' }}
              disabled={!allowChanges || ready.length === 0}
              onClick={openApply}
            />
          </div>
          <ul className={styles.changes}>
            {resolved.map(r => (
              <ChangeCard
                key={r.change.id}
                scan={full}
                plan={plan}
                resolved={r}
                decision={decisions[r.change.id]}
                conflicts={[]}
                onDecide={d => setDecisions(prev => ({ ...prev, [r.change.id]: d }))}
              />
            ))}
          </ul>
        </>
      )}

      {applying && (
        <ApplyPanel
          scan={full}
          plan={plan}
          changes={applying.changes}
          engine={applying.engine}
          auditLogUrl={auditLog.viewUrl}
          telemetry={telemetry}
          onDismiss={() => setApplying(undefined)}
          onRescan={() => { setApplying(undefined); onRescan(); }}
        />
      )}
    </div>
  );
}
